/**
 * useColumnizer - A composable for columnizing content into pages
 *
 * This handles the core logic of:
 * - Measuring message heights
 * - Fitting messages into columns
 * - Splitting long messages across columns/pages
 * - Creating page structures
 */

/**
 * Configuration for the columnizer
 * @typedef {Object} ColumnizerConfig
 * @property {number} columns - Number of columns (1, 2, or 3)
 * @property {number} pageHeight - Height of a page in pixels
 * @property {number} pageWidth - Width of a page in pixels
 * @property {number} headerOffset - Height reserved for page headers
 * @property {number} bottomPadding - Safety buffer at bottom of column (default: 0)
 * @property {string} pageClass - CSS class for column type (gc-column-1, gc-column-2, gc-column-3)
 * @property {string} pageClasses - Additional CSS classes for pages
 * @property {boolean} showPageNumbers - Whether to show page numbers
 */

/**
 * Creates a columnizer instance
 * @param {ColumnizerConfig} config
 * @returns {Object} Columnizer methods
 */
export function createColumnizer(config) {
  const {
    columns: rawColumns = 2,
    pageHeight = 765,
    pageWidth = 540,
    headerOffset = 17, // Reduced from 35 - just enough for page header text
    bottomPadding = 0, // Safety buffer at bottom of column to prevent messages touching border
    pageClass = "gc-column-2",
    pageClasses = "",
    showPageNumbers = true,
  } = config

  // Clamp columns to a valid value (1, 2, or 3). Anything else (0, null,
  // undefined, NaN, strings) would produce an empty columnSlots array and
  // crash at currentColumn.appendChild() when the first message is placed.
  const columns =
    rawColumns === 1 || rawColumns === 2 || rawColumns === 3 ? rawColumns : 2

  const maxColumnHeight = pageHeight - headerOffset - bottomPadding

  /**
   * Main columnization function
   * @param {HTMLElement[]} messageElements - Array of message DOM elements
   * @param {HTMLElement|null} targetContainer - Container to append pages to (optional if insertPage provided)
   * @param {Function} [insertPage] - Optional callback to insert page element (receives pageEl)
   * @returns {{ pages: number }} Result with page count
   */
  function columnize(messageElements, targetContainer, insertPage) {
    if (!messageElements.length) {
      return { pages: 0 }
    }

    // Helper to add page to DOM
    const addPage = (pageEl) => {
      if (insertPage) {
        insertPage(pageEl)
      } else if (targetContainer) {
        targetContainer.appendChild(pageEl)
      }
    }

    // Helper to get RENDERED image height (not intrinsic/natural dimensions)
    // The image must already be in a proper CSS context (staging area) for accurate measurement
    const getImageHeight = (msgEl, imgEl) => {
      if (!imgEl) return 0

      // Use getBoundingClientRect for accurate rendered dimensions
      const imgRect = imgEl.getBoundingClientRect()
      const renderedImgHeight = imgRect.height

      // Get the image container for margin calculation
      const imgContainer =
        imgEl.closest(".gc-message-photo-img") ||
        imgEl.closest(".gc-message-image") ||
        imgEl.parentElement

      // GTD-0828: this used to be an early-return that only checked the CONTAINER's
      // margin (.gc-message-photo-img — no margin set anywhere for 2-/3-column) and
      // never reached the "image's own margin" code below. The real margin-bottom
      // (Css/gt-mt-groupcard.css: .gc-column-2/3 .gc-column-outline img, 10px/6px) is
      // set on the <img> tag itself, not its container, so it was silently measured
      // as 0 for every message with an image — under-counting almost every message's
      // true rendered footprint by that margin and corrupting the fit/overflow math.
      // Sum both sources instead of checking only one and returning early; neither
      // is ever non-zero at the same time as the other in the current CSS, so this
      // can't double-count, and stays correct if that ever changes.
      let containerMarginTop = 0
      let containerMarginBottom = 0
      if (imgContainer && imgContainer !== msgEl) {
        const containerStyle = window.getComputedStyle(imgContainer)
        containerMarginTop = parseFloat(containerStyle.marginTop) || 0
        containerMarginBottom = parseFloat(containerStyle.marginBottom) || 0
      }

      const imgStyle = window.getComputedStyle(imgEl)
      const imgMarginTop = parseFloat(imgStyle.marginTop) || 0
      const imgMarginBottom = parseFloat(imgStyle.marginBottom) || 0

      return (
        renderedImgHeight +
        containerMarginTop +
        containerMarginBottom +
        imgMarginTop +
        imgMarginBottom
      )
    }

    // Measure all heights before moving
    const messageHeights = messageElements.map((el) => el.offsetHeight)

    // Inter-card gap, so the fit check below can reserve it. Without this the next
    // card passes msgHeight <= remaining but appending it also adds the gap on
    // top, quietly overfilling the column by that much at every card boundary.
    // Reads the gap from a card that isn't :last-child, since a :last-child rule
    // zeroes the margin there.
    const gapSource = messageElements.find(
      (el) => el.parentElement && el !== el.parentElement.lastElementChild
    )
    const colGap = gapSource
      ? parseFloat(window.getComputedStyle(gapSource).marginBottom) || 0
      : 0

    // Create first page
    let currentPage = createPage()
    addPage(currentPage.element)
    let pageCount = 1

    let currentColIndex = 0
    let currentColumn = currentPage.columns[currentColIndex]
    let currentColHeight = 0

    // Process each message following Column Layout Rules
    for (let i = 0; i < messageElements.length; i++) {
      const msgEl = messageElements[i]
      const msgHeight = messageHeights[i]
      // Reserve the gap that will be inserted before this card (only when the
      // column already holds one).
      const gapCost = currentColHeight > 0 ? colGap : 0
      const remainingHeight = maxColumnHeight - currentColHeight - gapCost
      const imgEl = msgEl.querySelector("img")
      const hasImage = !!imgEl

      // ONE-COLUMN MODE: each message gets its own page (matches Designs-Mockup behaviour)
      if (columns === 1 && i > 0) {
        const advanced = advanceColumn()
        currentColIndex = advanced.colIndex
        currentColumn = advanced.column
        currentPage = advanced.page
        pageCount = advanced.pageCount
        currentColHeight = 0
      }

      // ONE-COLUMN MODE: place directly, then paginate any body-text overflow.
      // We measure .gc-message-body scrollHeight vs clientHeight (which reflects
      // the flex-allocated space after placement) rather than the outer element's
      // forced height:100%, avoiding spurious word-by-word splitting.
      if (columns === 1) {
        currentColumn.appendChild(msgEl)
        let overflow = extractOneColBodyOverflow(msgEl)
        let safetyCount = 0
        while (overflow && safetyCount++ < 50) {
          const adv = advanceColumn()
          currentColIndex  = adv.colIndex
          currentColumn    = adv.column
          currentPage      = adv.page
          pageCount        = adv.pageCount
          currentColHeight = 0
          currentColumn.appendChild(overflow)
          overflow = extractOneColBodyOverflow(overflow)
        }
        currentColHeight = currentColumn.scrollHeight
        continue
      }

      // Rule 1: Message fully fits - place entire message
      if (msgHeight <= remainingHeight) {
        currentColumn.appendChild(msgEl)
        // Use actual rendered height to account for margin collapse
        currentColHeight = currentColumn.scrollHeight
        continue
      }

      // Rule 2: Message contains an image
      if (hasImage) {
        // Get image height - measure it in the staging context
        const imageHeight = getImageHeight(msgEl, imgEl)

        // Rule 2a: Image does NOT fit in remaining space - must move to new column
        if (imageHeight > remainingHeight) {
          // Move to new column
          const advanced = advanceColumn()
          currentColIndex = advanced.colIndex
          currentColumn = advanced.column
          currentPage = advanced.page
          pageCount = advanced.pageCount
          currentColHeight = 0

          // Place image at top, text below, split text if needed
          const result = splitAndPlace(msgEl, currentColumn)
          currentColHeight = result.usedHeight

          let overflow = result.overflow
          while (overflow) {
            const adv = advanceColumn()
            currentColIndex = adv.colIndex
            currentColumn = adv.column
            currentPage = adv.page
            pageCount = adv.pageCount
            currentColHeight = 0

            const overflowResult = splitAndPlace(overflow, currentColumn)
            currentColHeight = currentColumn.scrollHeight
            overflow = overflowResult.overflow
          }
          continue
        }

        // Rule 2b: Image FITS in remaining space - try to place image + as much text as fits
        // Use splitAndPlaceWithMax which will keep image intact and split only text
        const result = splitAndPlaceWithMaxForImage(
          msgEl,
          currentColumn,
          remainingHeight,
          imageHeight
        )

        // If no text fits with the image, move entire message to next column
        // This ensures image and at least some text stay together on the same page
        if (result.moveToNextColumn) {
          const advanced = advanceColumn()
          currentColIndex = advanced.colIndex
          currentColumn = advanced.column
          currentPage = advanced.page
          pageCount = advanced.pageCount
          currentColHeight = 0

          // Place image at top of new column, split text if needed
          const newResult = splitAndPlace(msgEl, currentColumn)
          currentColHeight = newResult.usedHeight

          let overflow = newResult.overflow
          while (overflow) {
            const adv = advanceColumn()
            currentColIndex = adv.colIndex
            currentColumn = adv.column
            currentPage = adv.page
            pageCount = adv.pageCount
            currentColHeight = 0

            const overflowResult = splitAndPlace(overflow, currentColumn)
            currentColHeight = currentColumn.scrollHeight
            overflow = overflowResult.overflow
          }
          continue
        }

        // Use actual column height to account for margin collapse
        currentColHeight = currentColumn.scrollHeight

        // Handle overflow text in next column(s)
        let overflow = result.overflow
        while (overflow) {
          const advanced = advanceColumn()
          currentColIndex = advanced.colIndex
          currentColumn = advanced.column
          currentPage = advanced.page
          pageCount = advanced.pageCount
          currentColHeight = 0

          const overflowResult = splitAndPlace(overflow, currentColumn)
          currentColHeight = currentColumn.scrollHeight
          overflow = overflowResult.overflow
        }
        continue
      }

      // Rule 3: Text-only message
      // Rule 3a: Does not fit AND remaining space >= 20px (one line of text)
      if (remainingHeight >= 20) {
        // Try to split text to fill remaining space
        const result = splitAndPlaceWithMax(
          msgEl,
          currentColumn,
          remainingHeight
        )

        // If single-line content doesn't fit in partial space, move to next column
        if (result.moveToNextColumn) {
          const advanced = advanceColumn()
          currentColIndex = advanced.colIndex
          currentColumn = advanced.column
          currentPage = advanced.page
          pageCount = advanced.pageCount
          currentColHeight = 0

          // Place in fresh column (full height available)
          const newResult = splitAndPlace(msgEl, currentColumn)
          currentColHeight = currentColumn.scrollHeight

          let overflow = newResult.overflow
          while (overflow) {
            const adv = advanceColumn()
            currentColIndex = adv.colIndex
            currentColumn = adv.column
            currentPage = adv.page
            pageCount = adv.pageCount
            currentColHeight = 0

            const overflowResult = splitAndPlace(overflow, currentColumn)
            currentColHeight = currentColumn.scrollHeight
            overflow = overflowResult.overflow
          }
          continue
        }

        // Use actual column height to account for margin collapse
        currentColHeight = currentColumn.scrollHeight

        // Continue overflow text in next column
        let overflow = result.overflow
        while (overflow) {
          const advanced = advanceColumn()
          currentColIndex = advanced.colIndex
          currentColumn = advanced.column
          currentPage = advanced.page
          pageCount = advanced.pageCount
          currentColHeight = 0

          const overflowResult = splitAndPlace(overflow, currentColumn)
          currentColHeight = currentColumn.scrollHeight
          overflow = overflowResult.overflow
        }
        continue
      }

      // Rule 3b: Does not fit AND remaining space < 20px
      // Move to new column, split only if it still overflows
      const advanced = advanceColumn()
      currentColIndex = advanced.colIndex
      currentColumn = advanced.column
      currentPage = advanced.page
      pageCount = advanced.pageCount
      currentColHeight = 0

      const result = splitAndPlace(msgEl, currentColumn)
      currentColHeight = currentColumn.scrollHeight

      let overflow = result.overflow
      while (overflow) {
        const adv = advanceColumn()
        currentColIndex = adv.colIndex
        currentColumn = adv.column
        currentPage = adv.page
        pageCount = adv.pageCount
        currentColHeight = 0

        const overflowResult = splitAndPlace(overflow, currentColumn)
        currentColHeight = currentColumn.scrollHeight
        overflow = overflowResult.overflow
      }
    }

    // Update page numbers
    if (targetContainer) {
      updatePageNumbers(targetContainer, pageCount)
    }

    return { pages: pageCount }

    // Helper: Advance to next column/page
    function advanceColumn() {
      currentColIndex++
      if (currentColIndex >= columns) {
        currentPage = createPage()
        addPage(currentPage.element)
        pageCount++
        currentColIndex = 0
      }
      return {
        colIndex: currentColIndex,
        column: currentPage.columns[currentColIndex],
        page: currentPage,
        pageCount,
      }
    }
  }

  /**
   * Split a message to fit within maxColumnHeight (full column)
   * @param {HTMLElement} msgEl - Message element
   * @param {HTMLElement} targetColumn - Target column
   * @returns {{ usedHeight: number, overflow: HTMLElement|null }}
   */
  function splitAndPlace(msgEl, targetColumn) {
    return splitAndPlaceWithMax(msgEl, targetColumn, maxColumnHeight)
  }

  /**
   * Split a message to fit within a specific max height
   * Splits by lines (atomic units) - never splits mid-line
   * @param {HTMLElement} msgEl - Message element
   * @param {HTMLElement} targetColumn - Target column
   * @param {number} maxHeight - Maximum height to fit within
   * @returns {{ usedHeight: number, overflow: HTMLElement|null, moveToNextColumn?: boolean }}
   */
  function splitAndPlaceWithMax(msgEl, targetColumn, maxHeight) {
    const textContainer = msgEl.querySelector(".gc-message-parent span")
    const nameEl = msgEl.querySelector(".gc-message-parent p")
    // Always measure with the name showing. An earlier placement attempt for this
    // same message can leave it hidden, and the fit search below would then size
    // the card without it and overfill the column by the name's height. The split
    // below re-hides it when this part isn't the last one.
    if (nameEl) nameEl.style.display = ""

    // Check if we're working with partial column space (not a fresh column)
    const isPartialColumn = maxHeight < maxColumnHeight

    // No text to split - just place it
    if (!textContainer) {
      targetColumn.appendChild(msgEl)
      return { usedHeight: msgEl.offsetHeight, overflow: null }
    }

    const originalHTML = textContainer.innerHTML
    
    // First try splitting by lines (atomic units)
    const lines = splitIntoLines(originalHTML)

    // Place in column to measure
    targetColumn.appendChild(msgEl)

    // If it fits within maxHeight, no split needed
    if (msgEl.offsetHeight <= maxHeight) {
      return { usedHeight: msgEl.offsetHeight, overflow: null }
    }

    // If only one line (or no line breaks), treat as atomic in partial space
    if (lines.length <= 1) {
      // If we're in partial column space, move to next column where there's more room
      if (isPartialColumn) {
        targetColumn.removeChild(msgEl)
        return { usedHeight: 0, overflow: null, moveToNextColumn: true }
      }
      // Full column but single line still doesn't fit - fall back to word splitting
      return splitByWords(msgEl, textContainer, nameEl, originalHTML, maxHeight)
    }

    // Binary search to find optimal split point by lines
    let low = 1
    let high = lines.length
    let bestFit = 0

    while (low <= high) {
      const mid = Math.floor((low + high) / 2)
      textContainer.innerHTML = lines.slice(0, mid).join("")

      if (msgEl.offsetHeight <= maxHeight) {
        bestFit = mid
        low = mid + 1
      } else {
        high = mid - 1
      }
    }

    // If no lines fit, move to next column if partial, or word-split if full
    if (bestFit === 0) {
      textContainer.innerHTML = originalHTML
      if (isPartialColumn) {
        targetColumn.removeChild(msgEl)
        return { usedHeight: 0, overflow: null, moveToNextColumn: true }
      }
      // Full column but first line doesn't fit - fall back to word splitting
      return splitByWords(msgEl, textContainer, nameEl, originalHTML, maxHeight)
    }

    // If all lines fit, no split needed
    if (bestFit >= lines.length) {
      textContainer.innerHTML = originalHTML
      return { usedHeight: msgEl.offsetHeight, overflow: null }
    }

    // We have bestFit complete lines that fit.
    // Now try to also squeeze in words from the next line to fill remaining space.
    const fittingLinesHTML = lines.slice(0, bestFit).join("")
    const nextLine = lines[bestFit]
    const remainingLinesAfterNext = lines.slice(bestFit + 1)
    const nextLineWords = splitIntoWords(nextLine)

    let wordFit = 0
    if (nextLineWords.length > 1) {
      // Binary search for how many words from the next line also fit
      let wLow = 1
      let wHigh = nextLineWords.length
      while (wLow <= wHigh) {
        const wMid = Math.floor((wLow + wHigh) / 2)
        textContainer.innerHTML = fittingLinesHTML + nextLineWords.slice(0, wMid).join("")
        if (msgEl.offsetHeight <= maxHeight) {
          wordFit = wMid
          wLow = wMid + 1
        } else {
          wHigh = wMid - 1
        }
      }
    }

    // Build the final content for this column
    let thisPartHTML
    let overflowHTML
    if (wordFit > 0 && wordFit < nextLineWords.length) {
      // Some words from the next line fit - include them
      thisPartHTML = fittingLinesHTML + nextLineWords.slice(0, wordFit).join("")
      overflowHTML = nextLineWords.slice(wordFit).join("") + remainingLinesAfterNext.join("")
    } else if (wordFit >= nextLineWords.length) {
      // All words from the next line fit - include the whole line
      thisPartHTML = fittingLinesHTML + nextLine
      overflowHTML = remainingLinesAfterNext.join("")
    } else {
      // No words from the next line fit - just use complete lines
      thisPartHTML = fittingLinesHTML
      overflowHTML = lines.slice(bestFit).join("")
    }

    textContainer.innerHTML = thisPartHTML

    // The word search above measured bare words, but the branches can append the
    // line's own trailing markup (a <br> etc.), which costs a line box that was
    // never measured — letting the chunk exceed maxHeight and overfill the column.
    // Re-check, and fall back to whole lines only if it does.
    if (msgEl.offsetHeight > maxHeight && thisPartHTML !== fittingLinesHTML) {
      thisPartHTML = fittingLinesHTML
      overflowHTML = lines.slice(bestFit).join("")
      textContainer.innerHTML = thisPartHTML
    }
    const usedHeight = msgEl.offsetHeight

    // Hide name from first part
    if (nameEl) {
      nameEl.style.display = "none"
    }

    // Create overflow element with remaining content
    // If all content fit (no overflow text), no overflow needed
    const overflowText = overflowHTML.replace(/<[^>]*>/g, "").replace(/&nbsp;/gi, " ").trim()
    if (!overflowText) {
      // Everything fit, restore name
      if (nameEl) nameEl.style.display = ""
      return { usedHeight, overflow: null }
    }

    const overflow = createOverflowMessage(
      msgEl,
      overflowHTML,
      nameEl
    )

    return { usedHeight, overflow }
  }

  /**
   * Word-level splitting fallback for when a single line/paragraph is longer than a full column
   * @param {HTMLElement} msgEl - Message element (already in DOM)
   * @param {HTMLElement} textContainer - Text span element
   * @param {HTMLElement|null} nameEl - Name paragraph element
   * @param {string} originalHTML - Original HTML content
   * @param {number} maxHeight - Maximum height to fit within
   * @returns {{ usedHeight: number, overflow: HTMLElement|null }}
   */
  function splitByWords(msgEl, textContainer, nameEl, originalHTML, maxHeight) {
    const words = splitIntoWords(originalHTML)

    // Can't split single word - place as-is
    if (words.length <= 1) {
      return { usedHeight: msgEl.offsetHeight, overflow: null }
    }

    // Binary search to find how many words fit
    let low = 1
    let high = words.length
    let bestFit = 1

    while (low <= high) {
      const mid = Math.floor((low + high) / 2)
      textContainer.innerHTML = words.slice(0, mid).join("")

      if (msgEl.offsetHeight <= maxHeight) {
        bestFit = mid
        low = mid + 1
      } else {
        high = mid - 1
      }
    }

    // If all words fit, restore and return
    if (bestFit >= words.length) {
      textContainer.innerHTML = originalHTML
      return { usedHeight: msgEl.offsetHeight, overflow: null }
    }

    // Apply word split
    textContainer.innerHTML = words.slice(0, bestFit).join("")
    const usedHeight = msgEl.offsetHeight

    // Hide name from first part
    if (nameEl) {
      nameEl.style.display = "none"
    }

    // Create overflow element with remaining words
    const overflow = createOverflowMessage(
      msgEl,
      words.slice(bestFit).join(""),
      nameEl
    )

    return { usedHeight, overflow }
  }

  /**
   * Split a message with image to fit within a specific max height
   * Image stays intact, text is split by lines (each line is atomic)
   * @param {HTMLElement} msgEl - Message element with image
   * @param {HTMLElement} targetColumn - Target column
   * @param {number} maxHeight - Maximum height to fit within
   * @param {number} imageHeight - Pre-measured image height
   * @returns {{ usedHeight: number, overflow: HTMLElement|null, moveToNextColumn?: boolean }}
   */
  function splitAndPlaceWithMaxForImage(
    msgEl,
    targetColumn,
    maxHeight,
    imageHeight
  ) {
    const textContainer = msgEl.querySelector(".gc-message-parent span")
    const nameEl = msgEl.querySelector(".gc-message-parent p")
    // Always measure with the name showing. An earlier placement attempt for this
    // same message can leave it hidden, and the fit search below would then size
    // the card without it and overfill the column by the name's height. The split
    // below re-hides it when this part isn't the last one.
    if (nameEl) nameEl.style.display = ""

    // No text to split - just place the image
    if (!textContainer) {
      targetColumn.appendChild(msgEl)
      return { usedHeight: msgEl.offsetHeight, overflow: null }
    }

    const originalHTML = textContainer.innerHTML
    
    // Split by lines - each line is an atomic unit
    const lines = splitIntoLines(originalHTML)

    // Place in column to measure
    targetColumn.appendChild(msgEl)

    // If it fits within maxHeight, no split needed
    if (msgEl.offsetHeight <= maxHeight) {
      return { usedHeight: msgEl.offsetHeight, overflow: null }
    }

    // If only one line (or no <br> tags), treat as atomic - move to next column
    if (lines.length <= 1) {
      // Single line with image that doesn't fit - move entire message to next column
      textContainer.innerHTML = originalHTML
      targetColumn.removeChild(msgEl)
      return { usedHeight: 0, overflow: null, moveToNextColumn: true }
    }

    // Binary search to find how many lines fit with the image
    // Start from 1 - we need at least one line of text with the image
    let low = 1
    let high = lines.length
    let bestFit = 0

    while (low <= high) {
      const mid = Math.floor((low + high) / 2)
      textContainer.innerHTML = lines.slice(0, mid).join("")

      if (msgEl.offsetHeight <= maxHeight) {
        bestFit = mid
        low = mid + 1
      } else {
        high = mid - 1
      }
    }

    // If no lines fit with the image (bestFit === 0), move entire message to next column
    if (bestFit === 0) {
      textContainer.innerHTML = originalHTML
      targetColumn.removeChild(msgEl)
      return { usedHeight: 0, overflow: null, moveToNextColumn: true }
    }

    // If all lines fit, restore and return
    if (bestFit >= lines.length) {
      textContainer.innerHTML = originalHTML
      return { usedHeight: msgEl.offsetHeight, overflow: null }
    }

    // We have bestFit complete lines that fit with the image.
    // Now try to also squeeze in words from the next line to fill remaining space.
    const fittingLinesHTML = lines.slice(0, bestFit).join("")
    const nextLine = lines[bestFit]
    const remainingLinesAfterNext = lines.slice(bestFit + 1)
    const nextLineWords = splitIntoWords(nextLine)

    let wordFit = 0
    if (nextLineWords.length > 1) {
      let wLow = 1
      let wHigh = nextLineWords.length
      while (wLow <= wHigh) {
        const wMid = Math.floor((wLow + wHigh) / 2)
        textContainer.innerHTML = fittingLinesHTML + nextLineWords.slice(0, wMid).join("")
        if (msgEl.offsetHeight <= maxHeight) {
          wordFit = wMid
          wLow = wMid + 1
        } else {
          wHigh = wMid - 1
        }
      }
    }

    // Build the final content for this column
    let thisPartHTML
    let overflowHTML
    if (wordFit > 0 && wordFit < nextLineWords.length) {
      thisPartHTML = fittingLinesHTML + nextLineWords.slice(0, wordFit).join("")
      overflowHTML = nextLineWords.slice(wordFit).join("") + remainingLinesAfterNext.join("")
    } else if (wordFit >= nextLineWords.length) {
      thisPartHTML = fittingLinesHTML + nextLine
      overflowHTML = remainingLinesAfterNext.join("")
    } else {
      thisPartHTML = fittingLinesHTML
      overflowHTML = lines.slice(bestFit).join("")
    }

    textContainer.innerHTML = thisPartHTML

    // The word search above measured bare words, but the branches can append the
    // line's own trailing markup (a <br> etc.), which costs a line box that was
    // never measured — letting the chunk exceed maxHeight and overfill the column.
    // Re-check, and fall back to whole lines only if it does.
    if (msgEl.offsetHeight > maxHeight && thisPartHTML !== fittingLinesHTML) {
      thisPartHTML = fittingLinesHTML
      overflowHTML = lines.slice(bestFit).join("")
      textContainer.innerHTML = thisPartHTML
    }
    const usedHeight = msgEl.offsetHeight

    // Hide name in first part (will show in overflow)
    if (nameEl) {
      nameEl.style.display = "none"
    }

    // Create overflow with remaining content (no image)
    const overflowText = overflowHTML.replace(/<[^>]*>/g, "").replace(/&nbsp;/gi, " ").trim()
    if (!overflowText) {
      if (nameEl) nameEl.style.display = ""
      return { usedHeight, overflow: null }
    }

    const overflow = createOverflowMessage(msgEl, overflowHTML, nameEl)

    return { usedHeight, overflow }
  }

  /**
   * Split HTML into lines using block-level boundaries as delimiters
   * Handles: <br>, </div><div>, </div>, <div>, </p><p>
   * Each line is treated as an atomic unit that won't be split
   * @param {string} html
   * @returns {string[]}
   */
  function splitIntoLines(html) {
    // Match block-level boundaries: div and p tags, br tags.
    // Order matters: longer patterns first to avoid partial matches.
    const lineBreakPattern = /(<div><br\s*\/?><\/div>|<p><br\s*\/?><\/p>|<\/div>\s*<div[^>]*>|<\/p>\s*<p[^>]*>|<br\s*\/?>|<\/div>|<div[^>]*>|<\/p>|<p[^>]*>)/gi

    const parts = html.split(lineBreakPattern)

    const result = []
    let current = ""

    for (let i = 0; i < parts.length; i++) {
      const part = parts[i]
      if (lineBreakPattern.test(part)) {
        // Reset lastIndex since we're reusing the regex
        lineBreakPattern.lastIndex = 0
        // Append the break tag to current content and push as a line
        current += part
        // Only push if there's actual visible text content
       const textContent = current.replace(/<[^>]*>/g, "").replace(/&nbsp;/gi, " ").trim()
       const hasSpacing = /<br\s*\/?>|<div[^>]*>|<p[^>]*>/.test(current)
       if (textContent || hasSpacing) {
          result.push(current)
        }
        current = ""
      } else {
        current += part
      }
    }

    // Push any remaining content
    const remainingText = current.replace(/<[^>]*>/g, "").replace(/&nbsp;/gi, " ").trim()
    if (remainingText) {
      result.push(current)
    }

    return result
  }

  /**
   * Split HTML into words while preserving tags
   * Used as fallback when a single line/paragraph is longer than a full column
   * @param {string} html
   * @returns {string[]}
   */
  function splitIntoWords(html) {
    const result = []
    let current = ""
    let inTag = false

    for (const char of html) {
      if (char === "<") {
        inTag = true
        current += char
      } else if (char === ">") {
        inTag = false
        current += char
      } else if (char === " " && !inTag) {
        current += char
        result.push(current)
        current = ""
      } else {
        current += char
      }
    }

    if (current) result.push(current)
    return result
  }

  /**
   * Create overflow message element
   * @param {HTMLElement} originalMsg
   * @param {string} textHTML
   * @param {HTMLElement|null} nameEl
   * @returns {HTMLElement}
   */
  function createOverflowMessage(originalMsg, textHTML, nameEl) {
    const clone = originalMsg.cloneNode(true)

    // Remove image container (stays with first part); fall back to bare img
    const imgContainer = clone.querySelector(".gc-message-photo-img")
    if (imgContainer) {
      imgContainer.remove()
    } else {
      const img = clone.querySelector("img")
      if (img) img.remove()
    }

    // Set overflow text
    const textContainer = clone.querySelector(".gc-message-parent span")
    if (textContainer) {
      textContainer.innerHTML = textHTML
    }

    // Restore name visibility
    const clonedName = clone.querySelector(".gc-message-parent p")
    if (clonedName && nameEl) {
      clonedName.style.display = ""
    }

    clone.classList.add("gc-message-continuation")
    clone.classList.remove("dontsplit")

    return clone
  }

  /**
   * Create a new page element
   * @returns {{ element: HTMLElement, columns: HTMLElement[] }}
   */
  function createPage() {
    const pageEl = document.createElement("div")
    pageEl.className = `page generated ${pageClasses}`
    pageEl.style.display = "block"

    // Page header
    if (showPageNumbers) {
      const header = document.createElement("div")
      header.className =
        "mt-card-cover-header d-flex justify-content-between align-items-center"
      header.innerHTML = '<p class="mb-0 page-number"></p>'
      pageEl.appendChild(header)
    }

    // Booklet structure
    const booklet = document.createElement("div")
    booklet.className = `gc-booklet ${pageClass}`

    const bookletInner = document.createElement("div")
    bookletInner.className = "gc-booklet-inner page-height-gc-page-2"
    bookletInner.style.height = `${pageHeight}px`

    const container = document.createElement("div")
    container.className = "gc-container"

    const containerInner = document.createElement("div")
    containerInner.className = "gc-container-inner page-scale-gc-page-2"
    // Override CSS hardcoded height with inline style (needs !important to override CSS)
    containerInner.style.cssText = `height: ${pageHeight}px !important; width: ${pageWidth}px !important;`

    const columnInner = document.createElement("div")
    columnInner.className = `gc-column-inner ${
      showPageNumbers ? "parent-editable" : ""
    }`

    // Create column slots
    const columnSlots = []
    const slotWidth = `${100 / columns}%`
    for (let i = 0; i < columns; i++) {
      const colSlot = document.createElement("div")
      colSlot.className = "gc-col-slot"
      // Mirror styles used in the TS implementation so JS
      // behaves identically even if external CSS isn't applied
      colSlot.style.display = "inline-block"
      colSlot.style.verticalAlign = "top"
      colSlot.style.boxSizing = "border-box"
      colSlot.style.overflow = "hidden"
      colSlot.style.width = slotWidth
      columnInner.appendChild(colSlot)
      columnSlots.push(colSlot)
    }

    // Assemble DOM
    containerInner.appendChild(columnInner)
    container.appendChild(containerInner)
    bookletInner.appendChild(container)
    booklet.appendChild(bookletInner)
    pageEl.appendChild(booklet)

    return { element: pageEl, columns: columnSlots }
  }

  /**
   * Update page numbers
   * @param {HTMLElement} container
   * @param {number} pageCount
   */
  function updatePageNumbers(container, pageCount) {
    const pages = container.querySelectorAll(".page")
    pages.forEach((page, idx) => {
      const pageNum = page.querySelector(".page-number")
      if (pageNum) {
        pageNum.textContent = `Page ${idx + 1}`
      }
    })
  }

  /**
   * 1-column overflow helper.
   * After a message is placed in a generated page, the flex layout gives
   * .gc-message-body a definite clientHeight. If its text overflows that space
   * we split by lines (then words as fallback) and return an overflow element
   * to be placed on the next page.
   * @param {HTMLElement} msgEl
   * @returns {HTMLElement|null}
   */
  function extractOneColBodyOverflow(msgEl) {
    const body          = msgEl.querySelector(".gc-message-body")
    const textContainer = body && body.querySelector("span")
    const nameEl        = msgEl.querySelector(".gc-message-parent p")

    if (!body || !textContainer) return null
    if (body.clientHeight <= 0) return null
    if (body.scrollHeight <= body.clientHeight) return null

    const originalHTML = textContainer.innerHTML
    const maxH         = body.clientHeight

    // Line-based split (each visual line is atomic)
    const lines = splitIntoLines(originalHTML)
    if (lines.length > 1) {
      let lo = 1, hi = lines.length, best = 0
      while (lo <= hi) {
        const mid = Math.floor((lo + hi) / 2)
        textContainer.innerHTML = lines.slice(0, mid).join("")
        if (body.scrollHeight <= maxH) { best = mid; lo = mid + 1 }
        else                           { hi = mid - 1 }
      }
      if (best >= lines.length) { textContainer.innerHTML = originalHTML; return null }
      if (best > 0) {
        textContainer.innerHTML = lines.slice(0, best).join("")
        if (nameEl) nameEl.style.display = "none"
        return createOverflowMessage(msgEl, lines.slice(best).join(""), nameEl)
      }
      // best === 0: no full line fits → fall through to word split
    }

    // Word-level fallback
    textContainer.innerHTML = originalHTML
    const words = splitIntoWords(originalHTML)
    if (words.length <= 1) return null

    let wLo = 1, wHi = words.length, wBest = 1
    while (wLo <= wHi) {
      const mid = Math.floor((wLo + wHi) / 2)
      textContainer.innerHTML = words.slice(0, mid).join("")
      if (body.scrollHeight <= maxH) { wBest = mid; wLo = mid + 1 }
      else                           { wHi = mid - 1 }
    }
    if (wBest >= words.length) { textContainer.innerHTML = originalHTML; return null }
    textContainer.innerHTML = words.slice(0, wBest).join("")
    if (nameEl) nameEl.style.display = "none"
    return createOverflowMessage(msgEl, words.slice(wBest).join(""), nameEl)
  }

  return {
    columnize,
    createPage,
    maxColumnHeight,
    updatePageNumbers
  }
}

/**
 * Get column class from column count
 * @param {number} columns
 * @returns {string}
 */
export function getPageClass(columns) {
  return columns === 1
    ? "gc-column-1"
    : columns === 2
    ? "gc-column-2"
    : "gc-column-3"
}

/**
 * Get column width from column count
 * @param {number} columns
 * @returns {number}
 */
export function getColumnWidth(columns) {
  return columns === 1 ? 540 : columns === 2 ? 270 : 180
}

/**
 * Page size configurations at 96 DPI (standard web resolution)
 *
 * True dimensions at 96 DPI:
 * - A4 (210mm × 297mm): 794px × 1123px - narrower but taller
 * - US Letter (8.5in × 11in): 816px × 1056px - wider but shorter
 *
 * For display, we use A4's scale factor (540/794 = 0.68) for both:
 * - A4 at 68% scale: 540px × 764px
 * - Letter at 68% scale: 555px × 718px (wider but shorter)
 *
 * @type {Object.<string, {width: number, height: number, aspectRatio: number, name: string, dpiWidth: number, dpiHeight: number}>}
 */
export const PAGE_SIZES = {
  A4: {
    name: "A4",
    // True 96 DPI dimensions
    dpiWidth: 794,
    dpiHeight: 1123,
    // Scaled for display (scale factor = 540/794 = 0.68)
    width: 540,
    height: 764, // 1123 × 0.68 = 764
    aspectRatio: 1.4143, // height/width = 1123/794
  },
  LETTER: {
    name: "Letter",
    // True 96 DPI dimensions
    dpiWidth: 816,
    dpiHeight: 1056,
    // Scaled for display (same scale factor as A4 = 0.68)
    width: 555, // 816 × 0.68 = 555 (wider than A4)
    height: 718, // 1056 × 0.68 = 718 (shorter than A4)
    aspectRatio: 1.2941, // height/width = 1056/816
  },
}

/**
 * Get page size configuration by name
 * @param {string} sizeName - 'A4' or 'LETTER'
 * @returns {{width: number, height: number, aspectRatio: number, name: string}}
 */
export function getPageSize(sizeName = "A4") {
  const normalized = sizeName?.toUpperCase?.() || "A4"
  return PAGE_SIZES[normalized] || PAGE_SIZES.A4
}

/**
 * Get column width adjusted for page size
 * @param {number} columns - Number of columns (1, 2, or 3)
 * @param {string} pageSize - 'A4' or 'LETTER'
 * @returns {number}
 */
export function getColumnWidthForSize(columns, pageSize = "A4") {
  const size = getPageSize(pageSize)
  return Math.floor(size.width / columns)
}